/**
 * Geocoding Utilities
 * 
 * Converts between city names and coordinates using OpenStreetMap Nominatim API
 * Free, no API key required, but please use responsibly (max 1 request per second)
 */

export interface LocationData {
  city: string;
  latitude: number;
  longitude: number;
  country?: string;
  state?: string;
  displayName?: string;
}

/**
 * Search for cities and return multiple suggestions
 * Uses OpenStreetMap Nominatim API (free, no key required)
 */
export async function searchCities(query: string, limit: number = 5): Promise<LocationData[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    // Use Nominatim API (OpenStreetMap) - free, no API key needed
    // Rate limit: 1 request per second
    const encodedQuery = encodeURIComponent(query);
    const url = `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=${limit}&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Fr00t App - Personal Astrology Profile' // Required by Nominatim
      }
    });

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((result: any) => {
      const address = result.address || {};
      const city = address.city || address.town || address.village || address.municipality || query;
      const state = address.state || address.region || '';
      const country = address.country || '';
      
      // Create a display name
      const parts = [city];
      if (state) parts.push(state);
      if (country) parts.push(country);
      const displayName = parts.join(', ');
      
      return {
        city,
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
        country,
        state,
        displayName: result.display_name || displayName
      };
    });
  } catch (error) {
    console.error('City search error:', error);
    return [];
  }
}

/**
 * Geocode a city name to coordinates
 * Uses OpenStreetMap Nominatim API (free, no key required)
 */
export async function geocodeCity(cityName: string): Promise<LocationData | null> {
  if (!cityName || cityName.trim().length === 0) {
    return null;
  }

  try {
    // Use Nominatim API (OpenStreetMap) - free, no API key needed
    // Rate limit: 1 request per second
    const encodedCity = encodeURIComponent(cityName);
    const url = `https://nominatim.openstreetmap.org/search?q=${encodedCity}&format=json&limit=1&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Fr00t App - Personal Astrology Profile' // Required by Nominatim
      }
    });

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data || data.length === 0) {
      return null;
    }

    const result = data[0];
    const address = result.address || {};
    
    return {
      city: address.city || address.town || address.village || address.municipality || cityName,
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      country: address.country,
      state: address.state || address.region,
      displayName: result.display_name
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

/**
 * Reverse geocode coordinates to city name
 */
export async function reverseGeocode(latitude: number, longitude: number): Promise<LocationData | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Fr00t App - Personal Astrology Profile'
      }
    });

    if (!response.ok) {
      throw new Error(`Reverse geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data || !data.address) {
      return null;
    }

    const address = data.address;
    
    return {
      city: address.city || address.town || address.village || address.municipality || 'Unknown',
      latitude,
      longitude,
      country: address.country,
      state: address.state || address.region,
      displayName: data.display_name
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

/**
 * Get current location using browser geolocation API
 */
export function getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
}

/**
 * Get current location and reverse geocode to get city name
 */
export async function getCurrentLocationWithCity(): Promise<LocationData | null> {
  try {
    const coords = await getCurrentLocation();
    return await reverseGeocode(coords.latitude, coords.longitude);
  } catch (error) {
    console.error('Error getting current location:', error);
    return null;
  }
}

